import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
  ServicoService,
  TipoServicoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { I18nService, MoedaPipe } from '@nomoh-monorepo/data-access-shared-i18n';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  DeviceService,
  FormFieldComponent,
  NMultiselectComponent,
} from '@nomoh-monorepo/shared-common-ui';
import { IEstabelecimento, IServico } from '@nomoh-monorepo/shared-types';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'lib-etapa-servicos',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    DatePickerModule,
    InputGroupModule,
    InputGroupAddonModule,
    FormFieldComponent,
    NMultiselectComponent,
    MoedaPipe,
    TranslateModule,
  ],
  templateUrl: './etapa-servicos.component.html',
  styleUrls: ['./etapa-servicos.component.scss'],
})
export class EtapaServicosComponent implements AfterViewInit {
  private i18n = inject(I18nService);
  simboloMoeda = this.i18n.$simboloMoeda;
  private readonly DURACAO_MINIMA_MINUTOS = 30;

  @ViewChild('headerTemplate')
  private headerTemplateRef?: TemplateRef<unknown>;
  @ViewChild('footerTemplate')
  private footerTemplateRef?: TemplateRef<unknown>;

  @Output() proximaEtapa = new EventEmitter<void>();
  @Output() etapaAnterior = new EventEmitter<void>();
  @Output() fechar = new EventEmitter<void>();
  @Output() headerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();

  private fb = inject(FormBuilder);
  private servicoService = inject(ServicoService);
  private tipoServicoDataService = inject(TipoServicoDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private estabelecimentoService = inject(EstabelecimentoService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  readonly deviceService = inject(DeviceService);

  form: FormGroup;
  servicosSignal = signal<Partial<IServico>[]>([]);
  servicosCadastradosSignal = signal<string[]>([]);
  $loading = signal<boolean>(false);

  // Signals dos DataServices
  $tiposServico = this.tipoServicoDataService.$tiposServico;
  $estabelecimentoAtual = this.estabelecimentoDataService.$estabelecimentoAtual;

  estabelecimento: IEstabelecimento = {} as IEstabelecimento;

  // Computed para duração máxima baseada no estabelecimento
  duracaoMaximaDate = computed(() => {
    return this.calcularDuracaoMaximaEstabelecimento();
  });

  duracaoMaximaMinutos = computed(() => {
    const maxDate = this.duracaoMaximaDate();
    if (!maxDate) return 720; // 12h padrão
    return this.converterDuracaoParaMinutos(maxDate);
  });

  constructor() {
    this.form = this.fb.group({
      nome: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
      ],
      tipoServicoId: [null, Validators.required],
      duracaoTime: [
        this.criarDuracaoInicial(),
        [
          Validators.required,
          this.validarDuracaoMinima,
          this.validarDuracaoMaxima.bind(this),
        ],
      ],
      valor: [null, [Validators.min(0)]],
    });

    this.preencherFormulario();
  }

  ngAfterViewInit(): void {
    if (this.headerTemplateRef) {
      this.headerTemplateAction.emit(this.headerTemplateRef);
    }
    if (this.footerTemplateRef) {
      this.footerTemplateAction.emit(this.footerTemplateRef);
    }
  }

  private async preencherFormulario(): Promise<void> {
    this.$loading.set(true);
    this.estabelecimento =
      (await this.estabelecimentoService.findCompleto()) ||
      ({} as IEstabelecimento);
    if (this.estabelecimento?.servicos) {
      this.servicosSignal.update((servicos) => [
        ...servicos,
        ...(this.estabelecimento.servicos || []).map(
          (s) =>
            ({
              nome: s.nome,
              tipoServicoId: String(s.tipoServicoId ?? ''),
              duracao: s.duracao,
              valor: s.valor,
              estabelecimentoId: s.estabelecimentoId,
              status: true,
            } as Partial<IServico>)
        ),
      ]);

      this.servicosCadastradosSignal.set(
        this.estabelecimento.servicos?.map((t) => t.nome) || []
      );
    }
    this.$loading.set(false);
  }

  async adicionarServico() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const estabelecimentoId = await this.obterEstabelecimentoId();
    if (!estabelecimentoId) {
      console.error('Estabelecimento não encontrado após recarregar dados');
      return;
    }

    const duracaoSelecionada: Date | string | null = this.form.value
      .duracaoTime as Date | string | null;
    const duracaoMinutos = this.converterDuracaoParaMinutos(duracaoSelecionada);

    if (duracaoMinutos < this.DURACAO_MINIMA_MINUTOS) {
      this.form.get('duracaoTime')?.setErrors({ duracaoMinima: true });
      return;
    }

    const novoServico: Partial<IServico> = {
      nome: this.form.value.nome?.trim(),
      tipoServicoId: String(this.form.value.tipoServicoId ?? ''),
      duracao: duracaoMinutos,
      valor: this.form.value.valor,
      estabelecimentoId,
      status: true,
    };

    this.servicosSignal.update((servicos) => [...servicos, novoServico]);
    this.form.reset({
      nome: '',
      tipoServicoId: null,
      duracaoTime: this.criarDuracaoInicial(),
      valor: null,
    });
  }

  removerServico(index: number) {
    if (
      this.servicosCadastradosSignal().includes(
        this.servicosSignal()[index].nome || ''
      )
    ) {
      const idServico = this.estabelecimento?.servicos?.find(
        (s) => s.nome === this.servicosSignal()[index].nome
      )?.id;
      this.servicoService.deletar(idServico || '');
    }
    this.servicosSignal.update((servicos) =>
      servicos.filter((_, i) => i !== index)
    );
  }

  nomeTipoServico(tipoServicoId: number | string): string {
    const tipo = this.$tiposServico().find(
      (t) => String(t.id) === String(tipoServicoId)
    );
    return tipo?.nome || '';
  }

  private async obterEstabelecimentoId(): Promise<string | undefined> {
    const idEmMemoria =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.id ??
      this.estabelecimento?.id;

    if (idEmMemoria) {
      return idEmMemoria;
    }

    const estabelecimento = await this.estabelecimentoService.findCompleto();
    if (estabelecimento) {
      this.estabelecimento = estabelecimento;
      return estabelecimento.id;
    }

    return undefined;
  }

  async salvar() {
    let servicos = this.servicosSignal();
    if (servicos.length === 0) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('PRIMEIRO_ACESSO.SERVICOS.NOTIFICACAO_MINIMO')
      );
      return;
    }

    try {
      this.$loading.set(true);
      if (this.servicosCadastradosSignal().length) {
        this.servicosSignal.set(
          this.servicosSignal().filter(
            (servico) =>
              !this.servicosCadastradosSignal().includes(servico.nome || '')
          )
        );
        servicos = this.servicosSignal();
      }

      const estabelecimentoId = await this.obterEstabelecimentoId();

      if (servicos.length > 0) {
        if (estabelecimentoId) {
          await this.servicoService.criarMultiplo(
            servicos.map((servico) => ({
              ...servico,
              estabelecimentoId,
            }))
          );
        } else {
          console.error(
            'Estabelecimento não encontrado após recarregar dados'
          );
        }
      }

      this.proximaEtapa.emit();
    } catch (error) {
      console.error('Erro ao salvar serviços:', error);
      this.notificacaoService.notificacaoError(
        this.translateService.instant('PRIMEIRO_ACESSO.SERVICOS.NOTIFICACAO_ERRO')
      );
    } finally {
      this.$loading.set(false);
    }
  }

  voltar() {
    this.etapaAnterior.emit();
  }

  private criarDuracaoInicial(): Date {
    return this.criarDateDuracao(this.DURACAO_MINIMA_MINUTOS);
  }

  private criarDateDuracao(minutos: number): Date {
    const base = new Date();
    base.setHours(Math.floor(minutos / 60), minutos % 60, 0, 0);
    return base;
  }

  private validarDuracaoMinima = (control: AbstractControl) => {
    const minutos = this.converterDuracaoParaMinutos(
      control.value as Date | string | null
    );
    if (minutos < this.DURACAO_MINIMA_MINUTOS) {
      return { duracaoMinima: true };
    }
    return null;
  };

  private converterDuracaoParaMinutos(valor?: Date | string | null): number {
    if (!valor) {
      return this.DURACAO_MINIMA_MINUTOS;
    }
    if (typeof valor === 'string') {
      const partes = valor.split(':');
      if (partes.length >= 2) {
        const h = Number(partes[0]);
        const m = Number(partes[1]);
        if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
      }
      return this.DURACAO_MINIMA_MINUTOS;
    }
    return valor.getHours() * 60 + valor.getMinutes();
  }

  /**
   * Converte string "HH:mm" para minutos totais
   */
  private parseHorario(horario: string): number {
    if (!horario || typeof horario !== 'string') {
      return 0;
    }

    const partes = horario.split(':');
    if (partes.length < 2) {
      return 0;
    }

    const h = Number(partes[0]);
    const m = Number(partes[1]);

    if (!Number.isFinite(h) || !Number.isFinite(m)) {
      return 0;
    }

    return h * 60 + m;
  }

  /**
   * Calcula a duração máxima permitida baseada no horário de funcionamento do estabelecimento
   */
  private calcularDuracaoMaximaEstabelecimento(): Date | null {
    const estabelecimento = this.$estabelecimentoAtual();

    if (!estabelecimento?.horaAbertura || !estabelecimento?.horaFechamento) {
      // Fallback: 12h (720 minutos)
      const base = new Date();
      base.setHours(12, 0, 0, 0);
      return base;
    }

    const inicioMinutos = this.parseHorario(estabelecimento.horaAbertura);
    const fimMinutos = this.parseHorario(estabelecimento.horaFechamento);

    let duracaoMinutos = fimMinutos - inicioMinutos;

    // Se negativo ou zerado, assumir virada de dia ou erro - usar 24h
    if (duracaoMinutos <= 0) {
      duracaoMinutos = 1440; // 24h
    }

    // Limite máximo de 24h
    if (duracaoMinutos > 1440) {
      duracaoMinutos = 1440;
    }

    const base = new Date();
    const horas = Math.floor(duracaoMinutos / 60);
    const minutos = duracaoMinutos % 60;
    base.setHours(horas, minutos, 0, 0);
    return base;
  }

  /**
   * Validator customizado para verificar se duração não excede o horário do estabelecimento
   */
  private validarDuracaoMaxima = (control: AbstractControl) => {
    const valor = control.value;
    if (!valor) {
      return null;
    }

    const duracaoSelecionada = this.converterDuracaoParaMinutos(valor);
    const duracaoMaxima = this.duracaoMaximaMinutos();

    if (duracaoSelecionada > duracaoMaxima) {
      return {
        duracaoMaxima: {
          maxMinutos: duracaoMaxima,
          atualMinutos: duracaoSelecionada,
        },
      };
    }

    return null;
  };

  /**
   * Formata duração em minutos para exibição (ex: "1h 30min")
   */
  formatarDuracaoExibicao(minutos: number): string {
    if (minutos <= 0) {
      return '0min';
    }

    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;

    if (horas === 0) {
      return `${mins}min`;
    }

    if (mins === 0) {
      return `${horas}h`;
    }

    return `${horas}h ${mins}min`;
  }
}
